import React, { useState, useEffect, useMemo } from "react";
import MyModal from "components/MyModal";
import { indetityApiAppToken } from "redux/services/identity";
import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  Box,
} from "@chakra-ui/react";
import Alert from "components/Alert";
import { useDispatch } from "react-redux";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
} from "redux/services/membership";
import {
  PaymentMethod,
  paymentMethodOptions,
  Roles,
  payrollManagedByOptions,
} from "common";
import { getOrganisationMembership } from "utils";
import env from "env";
import { getAlertTitle, validateField } from "containers/CreateEnterpriseUser/CreateEnterpriseUser.helper";
import { useCreateEnterpriseUserService } from "containers/CreateEnterpriseUser/CreateEnterpriseUser.service";
import { useRoles } from "hooks/useRoles";
import LoadingOverlay from "components/LoadingOverlay";
import { CreateEnterpriseUserFormData, CreateEnterpriseUserProps, createEnterpriseUserSchema as schema } from "./CreateEnterpriseUser.type";


function CreateEnterpriseUser(props: CreateEnterpriseUserProps) {
  const { isOpen, setOpenModalCreate } = props;

  const { handleSubmit, isLoading, error, PSPName, isFetchingOrgData } =
    useCreateEnterpriseUserService(setOpenModalCreate);
  const dispatch = useDispatch();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );
  const { isPaymentOpsRole, isBUAdminPSP, isSharedServiceRole, isBUAdmin } = useRoles();

  const [formData, setFormData] = useState<CreateEnterpriseUserFormData>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    bussinessUnitName: "",
    bussinessUnitCode: "",
    paymentMethod: PaymentMethod.NORMAL_PAY,
    userRole: isPaymentOpsRole ? Roles.BusinessAdmin : Roles.BusinessApprover,
    serviceProviderCode: "",
    payrollManagedBy: "",
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    bussinessUnitName?: string;
    bussinessUnitCode?: string;
    paymentMethod?: string;
    serviceProviderCode?: string;
  }>({});

  const { data: organizationData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId || "",
      userId: profileData?.data?.userId || "",
    },
    { skip: !(profileData && membershipOrganisation && !isPaymentOpsRole) }
  );

  const payrollManagedBy = useMemo(() => {
    if (isBUAdmin) {
      return (
        payrollManagedByOptions.find(
          (pmb) => pmb.value === env.app.payrollManagedInHouseHR
        )?.label ?? env.app.payrollManagedInHouseHR
      );
    }

    if (isBUAdminPSP) {
      return (
        payrollManagedByOptions.find(
          (pmb) => pmb.value === env.app.payrollManagedPSP
        )?.label ?? env.app.payrollManagedPSP
      );
    }

    if (isSharedServiceRole) {
      return (
        payrollManagedByOptions.find(
          (pmb) => pmb.value === env.app.payrollManagedSharedService
        )?.label ?? env.app.payrollManagedSharedService
      );
    }

    return "";
  }, [isBUAdmin, isBUAdminPSP, isSharedServiceRole]);

  useEffect(() => {
    return () => {
      dispatch(indetityApiAppToken.util.resetApiState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen && organizationData && !isPaymentOpsRole) {
      setFormData({
        firstName: "",
        lastName: "",
        emailAddress: "",
        bussinessUnitName: organizationData?.data?.companyName,
        bussinessUnitCode: organizationData?.data?.leiNumber,
        serviceProviderCode: organizationData?.data?.companyNumber,
        paymentMethod:
          organizationData?.data?.tradingName || PaymentMethod.NORMAL_PAY,
        userRole: "",
        payrollManagedBy: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, organizationData]);

  const handleChange = async (e: any, useTrim?: boolean) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: useTrim ? value.trim() : value,
    }));

    if (name === "bussinessUnitCode" || name === "serviceProviderCode") {
      const errorObj = await validateField(name, value, formData);
      setErrors((prev) => ({
        ...prev,
        ...errorObj,
      }));
    }
  };

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(formData, {
          abortEarly: false,
          context: { isPaymentOpsRole },
        });

        setIsFormValid(true);
      } catch {
        setIsFormValid(false);
      }
    };

    validate();
  }, [formData, isPaymentOpsRole]);

  return (
    <MyModal
      isOpen={isOpen}
      onClose={() => setOpenModalCreate()}
      title={
        isPaymentOpsRole
          ? "Register Business Administrator"
          : "Register Enterprise User"
      }
      submitBtnText="Register"
      cancelBtnText="Cancel"
      onSubmit={() => handleSubmit(formData, schema)}
      loadingSubmit={isLoading}
      scrollBehavior="inside"
      disabledCloseOutSide
      disableSubmit={!isFormValid}
    >
      {error && (
        <Alert
          data-testid="alert-error"
          status="error"
          mb="10px"
          title={getAlertTitle(error as any)}
        />
      )}

      <Box
        position="relative"
        m="0"
        p="0"
        width="100%"
        height="100%"
      >
        <Box position="absolute"
          top="0"
          left="0"
          w="100%"
          h="100%"
          backdropBlur={"2px"}
          zIndex={1}
          backgroundColor={"whiteAlpha.800"}
          display={isFetchingOrgData ? "flex" : "none"}
          justifyContent="center"
          alignItems="center"
        >
          <LoadingOverlay height="20px" />
        </Box>

        {(isBUAdmin || isBUAdminPSP || isSharedServiceRole) && (
          <FormControl mb="12px">
            <FormLabel>Payroll is Managed by</FormLabel>
            <Input
              value={payrollManagedBy}
              data-testid="pmb-create-enterprise"
              disabled
            />
          </FormControl>
        )}

        {(isBUAdminPSP || isSharedServiceRole) && (
          <FormControl mb="12px">
            <FormLabel>Payroll Service Providers name</FormLabel>
            <Input value={PSPName} data-testid="pmb-create-enterprise" disabled />
          </FormControl>
        )}

        <FormControl mb="12px" isInvalid={!!errors.emailAddress}>
          <FormLabel>Email Address</FormLabel>
          <Input
            value={formData.emailAddress}
            name="emailAddress"
            data-testid="email-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.emailAddress && (
            <FormErrorMessage fontSize={12}>
              {errors.emailAddress}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.firstName}>
          <FormLabel>First Name</FormLabel>
          <Input
            value={formData.firstName}
            name="firstName"
            data-testid="firstname-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.firstName && (
            <FormErrorMessage fontSize={12}>{errors.firstName}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.lastName}>
          <FormLabel>Last Name</FormLabel>
          <Input
            value={formData.lastName}
            name="lastName"
            data-testid="lastname-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.lastName && (
            <FormErrorMessage fontSize={12}>{errors.lastName}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.bussinessUnitName}>
          <FormLabel>Business Unit Name</FormLabel>
          <Input
            value={formData.bussinessUnitName}
            name="bussinessUnitName"
            data-testid="bussinessUnitName-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={!isPaymentOpsRole || isLoading}
          />
          {errors.bussinessUnitName && (
            <FormErrorMessage fontSize={12}>
              {errors.bussinessUnitName}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.serviceProviderCode}>
          <FormLabel>Service Provider Code</FormLabel>
          <Input
            value={formData.serviceProviderCode}
            name="serviceProviderCode"
            data-testid="serviceProvideCode-create-enterprise"
            isRequired
            onChange={(e) => handleChange(e, true)}
            maxLength={15}
            disabled={!isPaymentOpsRole || isLoading}
          />
          {errors.serviceProviderCode && (
            <FormErrorMessage fontSize={12}>
              {errors.serviceProviderCode}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.bussinessUnitCode}>
          <FormLabel>Business Unit Code</FormLabel>
          <Input
            value={formData.bussinessUnitCode}
            name="bussinessUnitCode"
            data-testid="bussinessUnitCode-create-enterprise"
            isRequired
            onChange={(e) => handleChange(e, true)}
            maxLength={15}
            disabled={!isPaymentOpsRole || isLoading}
          />
          {errors.bussinessUnitCode && (
            <FormErrorMessage fontSize={12}>
              {errors.bussinessUnitCode}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.paymentMethod}>
          <FormLabel>Payment Method</FormLabel>
          <Select
            name="paymentMethod"
            data-testid="paymentMethod-create-enterprise"
            isRequired
            onChange={handleChange}
            value={formData.paymentMethod}
            disabled={!isPaymentOpsRole || isLoading}
          >
            <option value="" disabled>
              --- Select a Payment Method ---
            </option>
            {paymentMethodOptions.map((option) => (
              <option value={option.value}>{option.label}</option>
            ))}
          </Select>
        </FormControl>
        {!isPaymentOpsRole && (
          <FormControl mb="12px">
            <FormLabel>User Role</FormLabel>
            <Select
              name="userRole"
              data-testid="userRole-create-enterprise"
              isRequired
              onChange={handleChange}
              value={formData.userRole}
              disabled={isLoading}
            >
              <option value="" disabled>
                --- Select a User Role ---
              </option>
              {isBUAdmin && (
                <>
                  <option value={Roles.BusinessOwner}>Business Maker</option>
                  <option value={Roles.BusinessApprover}>Business Checker</option>
                  <option value={Roles.BUApprover}>Business Approver</option>
                </>
              )}

              {isBUAdminPSP && (
                <option value={Roles.BUApprover}>Business Approver</option>
              )}
            </Select>
          </FormControl>
        )}

        {isPaymentOpsRole && (
          <FormControl mb="12px">
            <FormLabel>Payroll is Managed by</FormLabel>
            <Select
              name="payrollManagedBy"
              data-testid="payrollManagedBy-create-enterprise"
              isRequired
              onChange={handleChange}
              value={formData.payrollManagedBy}
              disabled={isLoading}
            >
              <option value="" disabled>
                --- Select a Payroll Managed By ---
              </option>
              {payrollManagedByOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </MyModal>
  );
}

export default CreateEnterpriseUser;
